import Axios from '@/utils/axios';
import { getBackendErrorMessage } from '@/utils/errorHandler';
import { ApiEnvelope } from '@/types/package';
import { unwrap } from '@/utils/apiEnvelope';
import { EscalationStatus } from '@/types/rental';

// Dash Rent — GET rent/mgmt/v1/tracking: where the units ops may have to go
// and collect are right now, off the vehicle telemetry snapshot. Alva pushes
// its fixes; Electrum is pulled in rotation, so a fleet of ~200 units is
// refreshed every few minutes. Polling every 5–10 minutes is therefore as
// fresh as the data gets.
const BASE = '/rent/mgmt/v1/tracking';

export interface RentalTrackingPosition {
  latitude: number;
  longitude: number;
  speed: number | null;
  ignition: boolean | null;
  lastAt: string | null;
  /** Minutes since lastAt when the response was built; null if never fixed. */
  ageMinutes: number | null;
}

export interface RentalTrackingRepossession {
  ID: string;
  code: string;
  status: string;
  severity: string;
}

export interface RentalTrackingZone {
  cityCode: string | null;
  cityName: string | null;
  districtCode: string | null;
  districtName: string | null;
}

export interface RentalTrackingItem {
  rentalID: string;
  rentalCode: string;
  rentalStatus: string;
  escalationStatus: string;
  escalationUpdatedAt: string | null;
  driverID: number;
  driverName: string | null;
  driverCode: string | null;
  hubName: string | null;
  vehicleID: string | null;
  platNomor: string | null;
  modelName: string | null;
  oemName: string | null;
  businessUnit: string | null;
  arrears: { amount: number; days: number };
  /** Null when the unit has never reported a fix. */
  position: RentalTrackingPosition | null;
  /**
   * Where the last fix falls administratively, resolved by the backend's zone
   * job from core's Kemendagri boundaries. Null until that job has seen this
   * fix; a cityName with a null districtName means only the kabupaten resolved.
   */
  zone: RentalTrackingZone | null;
  /** The open repossession case on this rental, if any. */
  repossession: RentalTrackingRepossession | null;
}

export interface RentalTrackingResponse {
  /** The instant arrears and pin ages were evaluated at; null if absent. */
  asOf: string | null;
  items: RentalTrackingItem[];
}

export interface GetRentalTrackingParams {
  /** Absent = SHUTDOWN_PENDING, SHUTDOWN, REPO_REQUESTED. */
  escalationStatus?: EscalationStatus[];
  /** Default true: an open repossession case puts a unit on the map. */
  includeRepossessionCases?: boolean;
}

export const getRentalTracking = async (
  params: GetRentalTrackingParams = {}
): Promise<RentalTrackingResponse> => {
  try {
    const response = await Axios.get<ApiEnvelope<RentalTrackingResponse>>(
      BASE,
      {
        params: {
          escalationStatus: params.escalationStatus?.length
            ? params.escalationStatus.join(',')
            : undefined,
          includeRepossessionCases:
            params.includeRepossessionCases === undefined
              ? undefined
              : String(params.includeRepossessionCases),
        },
      }
    );
    const body = unwrap(response.data);
    return { asOf: body?.asOf ?? null, items: body?.items ?? [] };
  } catch (error: unknown) {
    throw new Error(getBackendErrorMessage(error));
  }
};

// ─── One unit in full (the panel beside the map) ─────────────────────────────
// A second endpoint, not more fields on the list: the contacts cost one
// driver-service call per driver.

export interface RentalTrackingDetailPosition extends RentalTrackingPosition {
  odometer: string | null;
  bms1Soc: string | null;
}

export interface DriverAddressParts {
  street: string;
  village?: string;
  district?: string;
  city: string;
  province?: string;
}

export interface DriverEmergencyContact {
  /** 'FAMILY' | 'NON_FAMILY' — the two KYC sections. */
  kind: string;
  name: string;
  /** Already an Indonesian label ("Istri", "Teman"). */
  relationship: string;
  phoneNumber: string;
  address?: string;
  cityName?: string;
}

export interface RentalTrackingDetail {
  asOf: string;
  rental: {
    ID: string;
    code: string;
    status: string;
    escalationStatus: string;
    escalationUpdatedAt: string | null;
    contractType: string | null;
    packageName: string | null;
    dailyPrice: number;
    handoverDate: string | null;
    /** The handover's time-of-day — when a charge turns overdue, not midnight. */
    dailyCutoffTime: string | null;
    timezone: string;
    hubName: string | null;
    hubCityName: string | null;
  };
  vehicle: {
    ID: string | null;
    platNomor: string | null;
    vin: string | null;
    oemName: string | null;
    modelName: string | null;
    businessUnit: string | null;
    position: RentalTrackingDetailPosition | null;
  };
  arrears: {
    days: number;
    amount: number;
    breakdown: { type: string; days: number; amount: number }[];
    oldestChargeDate: string | null;
    newestChargeDate: string | null;
    prepaid: { rent: number; installment: number };
  };
  driver: {
    ID: number;
    code: string | null;
    name: string | null;
    phoneNumber: string | null;
    address: DriverAddressParts | null;
    emergencyContacts: DriverEmergencyContact[];
    /** NOT_FOUND = the id has no record, so retrying cannot help; ERROR =
     *  driver-service failed, so it can. */
    contactStatus: 'OK' | 'NOT_FOUND' | 'ERROR';
    contactFetchedAt: string | null;
    contactError: string | null;
  };
  repossession: {
    ID: string;
    code: string;
    status: string;
    severity: string;
    assignedOpsTeam: string | null;
    createdAt: string;
  } | null;
}

export const getRentalTrackingDetail = async (
  rentalID: string
): Promise<RentalTrackingDetail> => {
  try {
    const response = await Axios.get<ApiEnvelope<RentalTrackingDetail>>(
      `${BASE}/${rentalID}`
    );
    return unwrap(response.data);
  } catch (error: unknown) {
    throw new Error(getBackendErrorMessage(error));
  }
};

/** GeoJSON MultiPolygon, positions as [lng, lat] — Mapbox's own order. */
export interface AreaBoundaryGeometry {
  type: 'MultiPolygon';
  coordinates: [number, number][][][];
}

export interface TrackingAreaBoundary {
  code: string;
  name: string;
  boundary: AreaBoundaryGeometry;
}

/** Backend cap; asking for more is rejected outright. */
export const MAX_AREA_BOUNDARIES = 200;

/**
 * Outlines for the map. Fleet keeps no geometry of its own — it reads core's
 * Kemendagri dataset — so an area missing from the answer means "not available
 * right now", which includes core being unreachable, not "no such area".
 */
export const getTrackingAreaBoundaries = async (
  level: 'CITY' | 'DISTRICT',
  codes: string[]
): Promise<TrackingAreaBoundary[]> => {
  if (codes.length === 0) return [];
  try {
    const response = await Axios.get<
      ApiEnvelope<{ areas: TrackingAreaBoundary[] }>
    >(`${BASE}/area-boundaries`, {
      params: { level, codes: codes.slice(0, MAX_AREA_BOUNDARIES).join(',') },
    });
    return unwrap(response.data).areas;
  } catch (error: unknown) {
    throw new Error(getBackendErrorMessage(error));
  }
};
